import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import ColumnSettings from "@/components/ColumnSettings";
import SavedViews from "@/components/SavedViews";
import ColumnHeader from "@/components/table/ColumnHeader";
import { useColumnPrefs, cellStyle } from "@/hooks/useColumnPrefs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, Loader2, ChevronLeft, ChevronRight, EyeOff, Settings2, X, Download, Rows3, Rows4 } from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { pageIconFor } from "@/components/PageIcon";

function fmt(n) { return typeof n === "number" ? n.toLocaleString() : n; }
function MaskedCell({ value }) {
  if (value === "••••••••") return <span className="text-slate-400 tracking-widest select-none">••••••••</span>;
  if (value === null || value === undefined || value === "") return <span className="text-slate-300">—</span>;
  return value;
}

function pinBodyClass(sticky) {
  if (!sticky) return "";
  return `bg-white ${sticky.last ? "shadow-[2px_0_4px_-2px_rgba(0,0,0,0.12)]" : ""} ${sticky.first ? "shadow-[-2px_0_4px_-2px_rgba(0,0,0,0.12)]" : ""}`;
}

export default function DataTable({
  dataset, columns, searchPlaceholder = "Search…", title, icon: Icon, illustration, section = false,
  showContactsNote = false, rowLink, filters = [], params = {}, headerActions = null, toolbarStart = null, hideHeader = false, bare = false,
  tableId, systemViews = [], onSystemView, activeViewId, compactToggle = false, refreshKey,
}) {
  const loc = useLocation();
  const autoIcon = section ? null : pageIconFor(loc.pathname, loc.search);
  const densityKey = `ted_density_${tableId || dataset}`;
  const [compact, setCompact] = useState(() => compactToggle && localStorage.getItem(densityKey) === "compact");
  const toggleCompact = () => {
    const next = !compact;
    setCompact(next);
    localStorage.setItem(densityKey, next ? "compact" : "comfortable");
  };
  const { masking } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [debouncedQ, setDebouncedQ] = useState(searchParams.get("q") || "");
  const [page, setPage] = useState(1);
  const [filterValues, setFilterValues] = useState({});
  const pageSize = 25;
  const [data, setData] = useState({ items: [], total: 0, total_pages: 1, page: 1, contacts_visible: true });
  const [loading, setLoading] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { prefs, displayed, sticky, apply, setWidth, setSort, applyAll, reset } = useColumnPrefs(tableId || dataset, columns);
  const sort = prefs.sort;
  const onSort = (key, dir) => { setSort(key, dir); setPage(1); };
  const applyView = (p, f, view) => {
    if (view?.scope) { onSystemView?.(view); return; }
    applyAll(p); setQ(f?.q || ""); setFilterValues(f?.values || {}); setPage(1);
  };

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const activeFilters = {};
  filters.forEach((f) => {
    const v = filterValues[f.param];
    if (f.type === "toggle") { if (v) activeFilters[f.param] = true; }
    else if (v && v !== "all") activeFilters[f.param] = v;
  });
  const filterKey = JSON.stringify(activeFilters);
  const paramsKey = JSON.stringify(params);
  useEffect(() => { setPage(1); }, [paramsKey]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/${dataset}`, { params: { q: debouncedQ || undefined, page, page_size: pageSize, mask: masking, sort: sort?.key, dir: sort?.dir, ...params, ...activeFilters } })
      .then((r) => active && setData(r.data))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
    // eslint-disable-next-line
  }, [dataset, debouncedQ, page, masking, filterKey, paramsKey, sort, refreshKey]);

  const [exporting, setExporting] = useState(false);
  const exportToExcel = async () => {
    setExporting(true);
    try {
      const EXPORT_SIZE = 100;
      const baseParams = (p) => ({ q: debouncedQ || undefined, page: p, page_size: EXPORT_SIZE, mask: masking, ...params, ...activeFilters });
      const first = await api.get(`/${dataset}`, { params: baseParams(1) });
      let rows = first.data.items || [];
      const totalPages = Math.min(first.data.total_pages || 1, 1000);
      for (let p = 2; p <= totalPages; p++) {
        const r = await api.get(`/${dataset}`, { params: baseParams(p) });
        rows = rows.concat(r.data.items || []);
      }
      const cols = displayed.filter((c) => !c.noExport);
      const esc = (v) => {
        if (v === null || v === undefined) return "";
        let s = typeof v === "object" ? JSON.stringify(v) : String(v);
        if (s === "••••••••") s = "";
        if (/[",\n\r]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
        return s;
      };
      const header = cols.map((c) => esc(c.label)).join(",");
      const body = rows.map((row) => cols.map((c) => esc(row[c.key])).join(",")).join("\r\n");
      const csv = `\uFEFF${header}\r\n${body}`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(title || dataset).replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${rows.length.toLocaleString()} row${rows.length === 1 ? "" : "s"} to Excel`);
    } catch (e) {
      toast.error("Could not export table");
    } finally {
      setExporting(false);
    }
  };

  const setFilter = (param, value) => { setFilterValues((p) => ({ ...p, [param]: value })); setPage(1); };
  const clearFilters = () => { setFilterValues({}); setQ(""); setPage(1); };
  const hasActive = Object.keys(activeFilters).length > 0 || q;

  const start = data.total === 0 ? 0 : (data.page - 1) * pageSize + 1;
  const end = Math.min(data.page * pageSize, data.total);
  const pageWindow = () => {
    const tp = data.total_pages, cur = data.page, win = [];
    const from = Math.max(1, cur - 2), to = Math.min(tp, from + 4);
    for (let i = Math.max(1, to - 4); i <= to; i++) win.push(i);
    return win;
  };

  return (
    <div className={bare ? "" : "px-6 lg:px-8 pt-3 lg:pt-4 pb-6 lg:pb-8"} data-testid={`page-${dataset}`}>
      {!hideHeader && <div className="flex flex-wrap items-center gap-3 mb-4">
        {(illustration || autoIcon) && <img src={illustration || autoIcon} alt="" className="hidden sm:block w-16 h-auto shrink-0 select-none transition-[transform,filter] duration-300 ease-out hover:-translate-y-1 hover:scale-[1.04] hover:drop-shadow-[0_8px_10px_rgba(15,23,42,0.18)] motion-reduce:transition-none motion-reduce:hover:transform-none" data-testid={`${dataset}-header-illustration`} />}
        {Icon && !illustration && !autoIcon && <span className="w-10 h-10 rounded-2xl bg-white text-slate-700 border border-black/5 shadow-card flex items-center justify-center"><Icon className="w-5 h-5" strokeWidth={1.75} /></span>}
        <div>
          <h1 className={section ? "ted-section-title" : "ted-page-title"}>{title}</h1>
          <p className="ted-subtle mt-1">{fmt(data.total)} records</p>
        </div>
        {headerActions && <div className="ml-auto">{headerActions}</div>}
      </div>}

      {showContactsNote && !data.contacts_visible && (
        <div className="mb-4 flex items-center gap-2 text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 w-fit">
          <EyeOff className="w-3.5 h-3.5" /> Contact details are masked{masking ? " (masking on)" : " for your role"}.
        </div>
      )}

      <div className="bg-white rounded-[18px] border border-black/[0.04] shadow-card overflow-hidden">
          <div className="flex flex-wrap items-center gap-2 px-5 py-3.5 border-b border-slate-200/70 bg-white" data-testid={`${dataset}-toolbar`}>
            {toolbarStart}
            {filters.map((f) => {
              if (f.type === "select")
                return (
                  <Select key={f.param} value={filterValues[f.param] || "all"} onValueChange={(v) => setFilter(f.param, v)}>
                    <SelectTrigger className="h-9 w-auto min-w-[150px] bg-white" data-testid={`${dataset}-filter-${f.param}`}>
                      <SelectValue placeholder={f.label} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{f.label}: All</SelectItem>
                      {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                );
              if (f.type === "text")
                return (
                  <Input key={f.param} value={filterValues[f.param] || ""} onChange={(e) => setFilter(f.param, e.target.value)}
                    placeholder={f.label} data-testid={`${dataset}-filter-${f.param}`} className="h-9 w-44 bg-white" />
                );
              return (
                <label key={f.param} className="flex items-center gap-2 text-sm text-slate-600 bg-white border border-slate-200 rounded-lg h-9 px-3">
                  <Switch checked={!!filterValues[f.param]} onCheckedChange={(v) => setFilter(f.param, v)} data-testid={`${dataset}-filter-${f.param}`} aria-label={f.label} />
                  {f.label}
                </label>
              );
            })}
            {hasActive && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 gap-1 text-slate-500" data-testid={`${dataset}-filter-clear`}>
                <X className="w-3.5 h-3.5" /> Clear
              </Button>
            )}
          <div className="text-xs text-slate-500 px-1 whitespace-nowrap" data-testid={`${dataset}-showing`}>
            {loading ? <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…</span>
              : <>Showing <span className="font-semibold text-slate-700">{fmt(start)}</span>–<span className="font-semibold text-slate-700">{fmt(end)}</span> of <span className="font-semibold text-slate-700">{fmt(data.total)}</span></>}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <SavedViews tableId={tableId || dataset} prefs={prefs} filters={{ q, values: filterValues }} onApplyView={applyView} systemViews={systemViews} initialActiveId={activeViewId} />
            {compactToggle && (
              <Button variant={compact ? "secondary" : "outline"} size="icon" className="h-9 w-9 shrink-0" onClick={toggleCompact} aria-pressed={compact}
                data-testid={`${dataset}-density-toggle`} title={compact ? "Comfortable rows" : "Compact rows"}>
                {compact ? <Rows3 className="w-4 h-4 text-slate-700" /> : <Rows4 className="w-4 h-4 text-slate-600" />}
              </Button>
            )}
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={exportToExcel} disabled={exporting || data.total === 0} data-testid={`${dataset}-export-button`} title="Export to Excel">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin text-slate-600" /> : <Download className="w-4 h-4 text-slate-600" />}
            </Button>
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => setSettingsOpen(true)} data-testid={`${dataset}-column-settings-button`} title="Table settings — columns, order, pin, resize">
              <Settings2 className="w-4 h-4 text-slate-600" />
            </Button>
            <div className="relative w-40 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder} data-testid={`${dataset}-search-input`} className="pl-9 h-9 rounded-full bg-slate-100 border-transparent" />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse" data-testid={`${dataset}-table`} data-density={compact ? "compact" : "comfortable"}>
            <thead>
              <tr className="bg-white text-left text-xs font-semibold text-slate-900 border-b border-slate-200">
                {displayed.map((c) => (
                  <ColumnHeader key={c.key} col={c} style={cellStyle(c, prefs.widths, sticky)} sticky={sticky[c.key]} onResize={setWidth} sort={sort} onSort={onSort} />
                ))}
              </tr>
            </thead>
            <tbody>
              {displayed.length === 0 && <tr><td className="px-4 py-16 text-center text-slate-400 text-sm">No columns selected.</td></tr>}
              {displayed.length > 0 && !loading && data.items.length === 0 && (
                <tr><td colSpan={displayed.length} className="px-4 py-16 text-center text-slate-400 text-sm">No records found{debouncedQ ? ` for “${debouncedQ}”` : ""}.</td></tr>
              )}
              {displayed.length > 0 && data.items.map((row) => (
                <tr key={row.operational_record_id || row.legacy_id}
                  onClick={rowLink ? () => navigate(rowLink(row)) : undefined}
                  className={`border-b border-slate-200/60 hover:bg-slate-100/70 transition-colors ${rowLink ? "cursor-pointer" : ""}`}
                  data-testid={`${dataset}-row-${row.legacy_id}`}>
                  {displayed.map((c) => (
                    <td key={c.key} style={cellStyle(c, prefs.widths, sticky)}
                      className={[compact ? "px-4 py-1.5 text-[13px]" : "px-6 py-4 text-sm", c.align === "right" ? "text-right" : "", c.mono ? "mono text-xs text-slate-500" : "", c.strong ? "font-semibold text-slate-900" : "font-normal text-slate-500", c.truncate ? "max-w-[240px] truncate" : "whitespace-nowrap", pinBodyClass(sticky[c.key])].join(" ")}
                      title={c.truncate && typeof row[c.key] === "string" ? row[c.key] : undefined}>
                      {c.render ? c.render(row[c.key], row) : <MaskedCell value={row[c.key]} />}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {data.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-500">Page {data.page} of {data.total_pages}</span>
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1} data-testid={`${dataset}-page-prev`}><ChevronLeft className="w-4 h-4" /></Button>
              {pageWindow().map((p) => (
                <button key={p} onClick={() => setPage(p)} data-testid={`${dataset}-page-${p}`}
                  className={`h-8 min-w-8 px-2.5 rounded-full text-sm font-medium transition-colors ${p === data.page ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{p}</button>
              ))}
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))} disabled={data.page >= data.total_pages} data-testid={`${dataset}-page-next`}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          </div>
        )}
      </div>

      <ColumnSettings open={settingsOpen} onOpenChange={setSettingsOpen} columns={columns}
        order={prefs.order} visibleKeys={prefs.visible} pinned={prefs.pinned} onApply={apply} onReset={reset} />
    </div>
  );
}
